import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '../common/Alert.js';
import expenseService from '../../services/ExpenseService.js';
import { Receipt } from 'lucide-react';
import { FormLayout, FormField, DetailCard } from '../common/index.js';

const AddExpense = () => {
  const [formData, setFormData] = useState({
    category: 'RENT',
    month: '',
    year: new Date().getFullYear().toString(),
    expense_date: new Date().toISOString().split('T')[0],
    amount: '',
    description: '',
    vendor_name: '',
    bill_number: '',
    remarks: '',
    status: 'pending',
    payment_mode: '',
    transaction_id: '',
    paid_to: ''
  });
  
  const [fileAttachment, setFileAttachment] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [categories, setCategories] = useState([]);
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const paymentModes = ['cash', 'upi', 'bank_transfer', 'cheque', 'card', 'online', 'petty_cash'];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

  useEffect(() => {
    fetchDropdownData();
    const currentMonth = new Date().toLocaleString('en-US', { month: 'long' }).toLowerCase();
    setFormData(prev => ({ ...prev, month: currentMonth }));
  }, []);

  const fetchDropdownData = async () => {
    try {
      const [categoriesRes, monthsRes] = await Promise.all([
        expenseService.getCategories(),
        expenseService.getMonths()
      ]);
      setCategories(categoriesRes.data || []);
      setMonths(monthsRes.data || []);
    } catch (err) {
      Alert.error('Failed to load dropdown data');
      console.error('Failed to load dropdown data', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('File must be less than 5MB');
        return;
      }
      setFileAttachment(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = () => {
    setFileAttachment(null);
    setFilePreview(null);
    const fileInput = document.getElementById('file-attachment-input');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await expenseService.create(formData, fileAttachment);
      Alert.success('Expense created successfully!');
      setTimeout(() => navigate('/expenses'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create expense');
    } finally {
      setLoading(false);
    }
  };

  const isPaidStatus = formData.status === 'paid';

  return (
    <FormLayout
      title="Add New Expense"
      icon={Receipt}
      onSubmit={handleSubmit}
      submitLabel="Create Expense"
      loading={loading}
      onCancel={() => navigate('/expenses')}
      maxWidth="max-w-7xl"
    >
      <DetailCard title="Basic Information">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
          <FormField label="Category" required>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="input-clean"
              required
            >
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Month" required>
            <select
              name="month"
              value={formData.month}
              onChange={handleChange}
              className="input-clean"
              required
            >
              <option value="">Select Month</option>
              {months.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Year" required>
            <select
              name="year"
              value={formData.year}
              onChange={handleChange}
              className="input-clean"
              required
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Expense Date" required>
            <input
              type="date"
              name="expense_date"
              value={formData.expense_date}
              onChange={handleChange}
              className="input-clean"
              required
            />
          </FormField>

          <FormField label="Amount (₹)" required>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              placeholder="Enter amount"
              min="0"
              step="0.01"
              className="input-clean"
              required
            />
          </FormField>

          <FormField label="Description" required>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter expense description"
              rows="1"
              className="input-clean"
              required
            />
          </FormField>
        </div>
      </DetailCard>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <DetailCard title="Vendor Information">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <FormField label="Vendor Name">
              <input
                type="text"
                name="vendor_name"
                value={formData.vendor_name}
                onChange={handleChange}
                placeholder="Enter vendor name"
                className="input-clean"
              />
            </FormField>

            <FormField label="Bill Number">
              <input
                type="text"
                name="bill_number"
                value={formData.bill_number}
                onChange={handleChange}
                placeholder="Enter bill number"
                className="input-clean"
              />
            </FormField>
          </div>
        </DetailCard>

        <DetailCard title="Status & Payment Details">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <FormField label="Status">
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="input-clean"
              >
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
              </select>
            </FormField>

            {isPaidStatus && (
              <>
                <FormField label="Payment Mode">
                  <select
                    name="payment_mode"
                    value={formData.payment_mode}
                    onChange={handleChange}
                    className="input-clean"
                  >
                    <option value="">Select Payment Mode</option>
                    {paymentModes.map((mode) => (
                      <option key={mode} value={mode}>
                        {mode.replace(/_/g, ' ').toUpperCase()}
                      </option>
                    ))}
                  </select>
                </FormField>

                <FormField label="Transaction ID">
                  <input
                    type="text"
                    name="transaction_id"
                    value={formData.transaction_id}
                    onChange={handleChange}
                    placeholder="Enter transaction ID"
                    className="input-clean"
                  />
                </FormField>

                <FormField label="Paid To">
                  <input
                    type="text"
                    name="paid_to"
                    value={formData.paid_to}
                    onChange={handleChange}
                    placeholder="Enter payee name"
                    className="input-clean"
                  />
                </FormField>
              </>
            )}
          </div>
        </DetailCard>
      </div>

      <DetailCard title="Additional Information">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
          <FormField label="File Attachment">
            <input
              id="file-attachment-input"
              type="file"
              accept="image/*,.pdf"
              onChange={handleFileChange}
              className="input-clean py-1"
            />
            <p className="text-xs text-gray-600 mt-1">
              Accepted formats: JPG, PNG, GIF, WebP, PDF (Max 5MB)
            </p>
            {filePreview && (
              <div className="mt-3">
                <div className="relative inline-block">
                  <img 
                    src={filePreview} 
                    alt="File Preview" 
                    className="w-32 h-32 sm:w-40 sm:h-40 object-cover border-2 border-gray-300 rounded-md"
                  />
                  <button
                    type="button"
                    onClick={removeFile}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                    title="Remove file"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}
          </FormField>

          <FormField label="Remarks">
            <textarea
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
              placeholder="Enter any additional remarks"
              rows="1"
              className="input-clean"
            />
          </FormField>
        </div>
      </DetailCard>
    </FormLayout>
  );
};

export default AddExpense;
